/**
 * Procedural low-poly models built from primitives. Replace any builder with a glTF
 * loaded from Blender later; callers only need a THREE.Group back.
 */
import * as THREE from 'three';
import { stylize } from './stylize.js';

const matCache = new Map<string, THREE.MeshLambertMaterial>();
export function mat(color: number, opts?: THREE.MeshLambertMaterialParameters) {
  const key = color + JSON.stringify(opts ?? {});
  let m = matCache.get(key);
  if (!m) { m = new THREE.MeshLambertMaterial({ color, ...opts }); stylize(m); matCache.set(key, m); }
  return m;
}
export function addMesh(parent: THREE.Object3D, geo: THREE.BufferGeometry, color: number, x: number, y: number, z: number, opts?: THREE.MeshLambertMaterialParameters) {
  const m = new THREE.Mesh(geo, mat(color, opts));
  m.position.set(x, y, z); m.castShadow = true; m.receiveShadow = true;
  parent.add(m);
  return m;
}
/**
 * A box with bevelled edges: corners and edges are pushed out onto a radius, so light rolls
 * over them instead of snapping. Cheap (3 segments per side) and smooth-normalled on the bevels.
 */
function roundedBox(w: number, h: number, d: number, r: number) {
  const g = new THREE.BoxGeometry(w, h, d, 3, 3, 3);
  const p = g.getAttribute('position') as THREE.BufferAttribute, n = g.getAttribute('normal') as THREE.BufferAttribute;
  const ix = Math.max(0, w / 2 - r), iy = Math.max(0, h / 2 - r), iz = Math.max(0, d / 2 - r);
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const cx = Math.max(-ix, Math.min(ix, x)), cy = Math.max(-iy, Math.min(iy, y)), cz = Math.max(-iz, Math.min(iz, z));
    const ox = x - cx, oy = y - cy, oz = z - cz, l = Math.hypot(ox, oy, oz);
    if (l < 1e-6) continue;
    p.setXYZ(i, cx + (ox / l) * r, cy + (oy / l) * r, cz + (oz / l) * r);
    n.setXYZ(i, ox / l, oy / l, oz / l);
  }
  return g;
}
/** Push each vertex of a polyhedron in or out a little (same amount wherever a vertex is shared), for an organic lump. */
function lumpy(g: THREE.BufferGeometry, amount: number, seed: number) {
  const p = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const h = Math.sin(Math.round(x * 97) * 12.9898 + Math.round(y * 97) * 78.233 + Math.round(z * 97) * 37.719 + seed) * 43758.5453;
    const k = 1 + ((h - Math.floor(h)) - 0.5) * 2 * amount;
    p.setXYZ(i, x * k, y * k, z * k);
  }
  g.computeVertexNormals();
  return g;
}
/**
 * Free the GPU buffers of everything under `o` once it has left the scene for good. Only
 * geometry: materials share compiled shaders, and freeing those would make the next model that
 * uses them stall while they recompile. A geometry that is still in use elsewhere simply uploads
 * again on its next draw, so this is always safe to call.
 */
export function freeTree(o: THREE.Object3D) {
  o.traverse((c: THREE.Object3D) => { const g = (c as THREE.Mesh).geometry as THREE.BufferGeometry | undefined; if (g?.dispose) g.dispose(); });
}

/**
 * Merge the still parts of a built object into one mesh per material, so a cottage or a wrecked
 * car is a handful of draw calls instead of dozens. Parts that move (flames, shutters) are kept
 * as they are via `keep`. Call it before the group is placed in the world.
 */
/** The same material, reading the baked occlusion from vertex colours (one clone per material). */
const AO_MATS = new WeakMap<THREE.Material, THREE.Material>();
function aoMaterial(m: THREE.Material) {
  let c = AO_MATS.get(m);
  if (!c) {
    c = m.clone(); (c as THREE.MeshLambertMaterial).vertexColors = true;
    // keep the shared lighting model and any wind: clone() keeps onBeforeCompile, but the styled flag must follow
    c.userData = { ...m.userData }; c.onBeforeCompile = m.onBeforeCompile; c.customProgramCacheKey = () => `${m.customProgramCacheKey()}|ao`;
    AO_MATS.set(m, c);
  }
  return c;
}
export function mergeStatic(g: THREE.Object3D, keep: (m: THREE.Mesh) => boolean = () => false) {
  g.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(g.matrixWorld).invert();
  const byMat = new Map<THREE.Material, THREE.BufferGeometry[]>();
  const gone: THREE.Mesh[] = [];
  g.traverse((o: THREE.Object3D) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || (m as unknown as THREE.InstancedMesh).isInstancedMesh || Array.isArray(m.material) || keep(m)) return;
    const material = m.material as THREE.Material & { vertexColors?: boolean };
    if (material.vertexColors || !m.geometry.getAttribute('normal')) return;
    const geo = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone();
    for (const k of Object.keys(geo.attributes)) if (k !== 'position' && k !== 'normal') geo.deleteAttribute(k);
    geo.applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld));
    let list = byMat.get(material); if (!list) { list = []; byMat.set(material, list); }
    list.push(geo); gone.push(m);
  });
  if (gone.length < 2) { for (const l of byMat.values()) l.forEach(x => x.dispose()); return; }
  for (const m of gone) m.parent?.remove(m);
  for (const [material, geos] of byMat) {
    let n = 0; for (const x of geos) n += x.getAttribute('position').count;
    const pos = new Float32Array(n * 3), nor = new Float32Array(n * 3);
    let o = 0;
    for (const x of geos) { pos.set(x.getAttribute('position').array as Float32Array, o * 3); nor.set(x.getAttribute('normal').array as Float32Array, o * 3); o += x.getAttribute('position').count; x.dispose(); }
    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    // ambient occlusion baked in: darker near the ground, under eaves and overhangs (faces that
    // look down), so buildings and props sit in the world instead of floating on it
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const y = pos[i * 3 + 1], ny = nor[i * 3 + 1];
      const foot = 0.6 + 0.4 * Math.min(1, Math.max(0, y / 1.1)) ** 0.7;
      const under = ny < -0.3 ? 0.8 : 1;
      const k = foot * under;
      col[i * 3] = k; col[i * 3 + 1] = k; col[i * 3 + 2] = k;
    }
    merged.setAttribute('color', new THREE.BufferAttribute(col, 3));
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, aoMaterial(material));
    mesh.castShadow = true; mesh.receiveShadow = true;
    g.add(mesh);
  }
}
/**
 * For animated rigs: at every joint, merge the plain meshes hanging directly off it (per
 * material), leaving the joints themselves alone so they still move. A character goes from about
 * forty draw calls to a dozen. `skip` joints (held tools) are left as they are.
 */
export function mergeRig(root: THREE.Object3D, skip: THREE.Object3D[] = []) {
  const nodes: THREE.Object3D[] = [];
  root.traverse((o: THREE.Object3D) => { if (!(o as THREE.Mesh).isMesh && !skip.includes(o)) nodes.push(o); });
  for (const node of nodes) {
    const byMat = new Map<THREE.Material, THREE.Mesh[]>();
    for (const c of node.children) {
      const m = c as THREE.Mesh;
      if (!m.isMesh || m.children.length || (m as unknown as THREE.InstancedMesh).isInstancedMesh || Array.isArray(m.material)) continue;
      const material = m.material as THREE.Material & { vertexColors?: boolean };
      if (material.vertexColors || !m.geometry.getAttribute('normal') || !m.visible) continue;
      let l = byMat.get(material); if (!l) { l = []; byMat.set(material, l); }
      l.push(m);
    }
    for (const [material, list] of byMat) {
      if (list.length < 2) continue;
      const geos = list.map(m => { m.updateMatrix(); const g = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone(); for (const k of Object.keys(g.attributes)) if (k !== 'position' && k !== 'normal') g.deleteAttribute(k); g.applyMatrix4(m.matrix); return g; });
      let n = 0; for (const g of geos) n += g.getAttribute('position').count;
      const pos = new Float32Array(n * 3), nor = new Float32Array(n * 3);
      let o = 0;
      for (const g of geos) { pos.set(g.getAttribute('position').array as Float32Array, o * 3); nor.set(g.getAttribute('normal').array as Float32Array, o * 3); o += g.getAttribute('position').count; g.dispose(); }
      const merged = new THREE.BufferGeometry();
      merged.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      merged.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
      merged.computeBoundingSphere();
      const mesh = new THREE.Mesh(merged, material);
      mesh.castShadow = list[0].castShadow; mesh.receiveShadow = true;
      for (const m of list) node.remove(m);
      node.add(mesh);
    }
  }
}
/** Is `o` inside `root` (or root itself)? */
export const within = (o: THREE.Object3D, root: THREE.Object3D) => { for (let x: THREE.Object3D | null = o; x; x = x.parent) if (x === root) return true; return false; };

/** Geometry factory. Everything in the game is built from these, so detail is set here once. */
export const G = {
  box: (w: number, h: number, d: number) => roundedBox(w, h, d, Math.min(0.045, Math.min(w, h, d) * 0.22)),
  /** A crisp box, for planks and stripes that should stay sharp. */
  sharp: (w: number, h: number, d: number) => new THREE.BoxGeometry(w, h, d),
  /** A box with a chosen bevel radius (heads, torsos). */
  round: (w: number, h: number, d: number, r: number) => roundedBox(w, h, d, r),
  // four sides or fewer is a deliberate shape (a square roof, a pyramid); anything rounder gets smoother
  cyl: (a: number, b: number, h: number, s = 6) => new THREE.CylinderGeometry(a, b, h, s <= 4 ? s : Math.max(10, s * 2)),
  cone: (r: number, h: number, s = 6) => new THREE.ConeGeometry(r, h, s <= 4 ? s : Math.max(10, s * 2)),
  /** Leafy crowns and bushes: subdivided and lumpy. */
  ico: (r: number) => lumpy(new THREE.IcosahedronGeometry(r, 1), 0.09, r * 13),
  /** Rocks and stones: subdivided and lumpy, still faceted. */
  dod: (r: number) => lumpy(new THREE.DodecahedronGeometry(r, 1), 0.11, r * 7),
  oct: (r: number) => new THREE.OctahedronGeometry(r, 0),
  /** A craggy boulder: few big facets, pushed about so no two faces match. */
  crag: (r: number, seed = 1) => lumpy(new THREE.DodecahedronGeometry(r, 0), 0.2, seed),
  sphere: (r: number, s = 12) => new THREE.SphereGeometry(r, s, Math.max(6, Math.round(s * 0.7))),
  capsule: (r: number, len: number) => new THREE.CapsuleGeometry(r, len, 4, 10),
};

/** Flame meshes animate every frame; builders register them here. */
export const flames: THREE.Mesh[] = [];

type Builder = (g: THREE.Group) => void;
export const BUILDERS: Record<string, Builder> = {
  // a bank: an old-world safe, dragged in and bolted to a plank counter under a scrap awning
  chest(g) {
    addMesh(g, G.box(1.1, 0.55, 0.45), 0x7a5a3a, 0, 0.28, 0.32); addMesh(g, G.box(1.18, 0.06, 0.52), 0x8e6a42, 0, 0.58, 0.32);
    addMesh(g, G.box(0.7, 0.8, 0.55), 0x5a6068, 0, 0.4, -0.22); addMesh(g, G.box(0.5, 0.6, 0.04), 0x6a7078, 0, 0.42, 0.06);
    addMesh(g, G.cyl(0.09, 0.09, 0.05, 8).rotateX(Math.PI / 2), 0xc8b060, 0.1, 0.46, 0.09); addMesh(g, G.box(0.04, 0.16, 0.03), 0xc8b060, -0.14, 0.42, 0.09);
    for (const x of [-0.6, 0.6]) addMesh(g, G.box(0.06, 1.5, 0.06), 0x5a4a3a, x, 0.75, 0.55);
    addMesh(g, G.box(1.4, 0.05, 0.75), 0x8a4a2a, 0, 1.5, 0.35).rotation.x = 0.18;
    addMesh(g, G.box(0.7, 0.2, 0.03), 0xe8d8a0, 0, 1.2, 0.58);
  },
  // a deposit box: an old post box, painted over, with a slot
  deposit(g) {
    addMesh(g, G.cyl(0.24, 0.26, 0.9, 10), 0x8a3a2a, 0, 0.45, 0); addMesh(g, G.cyl(0.27, 0.27, 0.08, 10), 0x6a2a20, 0, 0.94, 0);
    addMesh(g, G.box(0.22, 0.04, 0.06), 0x1a1a1a, 0, 0.72, 0.24); addMesh(g, G.box(0.3, 0.06, 0.3), 0x5a5048, 0, 0.03, 0);
  },
  furnace(g) { addMesh(g, G.box(1.2, 1.1, 1.1), 0x8d8680, 0, 0.55, 0); addMesh(g, G.box(1.3, 0.16, 1.2), 0x76706a, 0, 1.12, 0); addMesh(g, G.box(0.5, 0.42, 0.06), 0xff8a2a, 0, 0.42, 0.56, { emissive: 0xff6a10 }); addMesh(g, G.box(0.38, 1.0, 0.38), 0x76706a, 0.25, 1.6, -0.25); },
  anvil(g) { addMesh(g, G.cyl(0.32, 0.38, 0.5, 8), 0x7a5634, 0, 0.25, 0); addMesh(g, G.box(0.7, 0.2, 0.3), 0x4a4d53, 0, 0.62, 0); addMesh(g, G.box(0.3, 0.18, 0.22), 0x4a4d53, 0, 0.47, 0); const h = addMesh(g, G.cone(0.13, 0.35, 6), 0x4a4d53, 0.5, 0.62, 0); h.rotation.z = -Math.PI / 2; },
  campfire(g) {
    for (let i = 0; i < 7; i++) { const a = (i / 7) * Math.PI * 2; addMesh(g, G.dod(0.14), 0x9a958f, Math.cos(a) * 0.42, 0.08, Math.sin(a) * 0.42); }
    for (let i = 0; i < 3; i++) { const l = addMesh(g, G.cyl(0.06, 0.06, 0.7, 5), 0x6a4a2e, 0, 0.12, 0); l.rotation.z = Math.PI / 2; l.rotation.y = (i * Math.PI) / 3; }
    const f1 = addMesh(g, G.cone(0.26, 0.7, 5), 0xffb347, 0, 0.45, 0, { emissive: 0xff8a1a }); f1.castShadow = false;
    const f2 = addMesh(g, G.cone(0.15, 0.5, 5), 0xfff0a0, 0, 0.4, 0, { emissive: 0xffd060 }); f2.castShadow = false;
    flames.push(f1, f2);
  },
  merchant(g) {
    for (const [x, z] of [[-0.6, -0.4], [0.6, -0.4], [-0.6, 0.4], [0.6, 0.4]]) addMesh(g, G.box(0.1, 1.6, 0.1), 0x7a5634, x, 0.8, z);
    addMesh(g, G.box(1.3, 0.55, 0.5), 0x9a6a3a, 0, 0.28, 0.25);
    for (let i = 0; i < 5; i++) addMesh(g, G.box(0.28, 0.08, 1.05), i % 2 ? 0xeee2c0 : 0x3a7fa0, -0.56 + i * 0.28, 1.65, 0);
    addMesh(g, G.box(0.35, 0.3, 0.35), 0xb7894f, -0.35, 0.7, 0.25); addMesh(g, G.dod(0.13), 0xe0863c, 0.2, 0.62, 0.3); addMesh(g, G.box(0.3, 0.1, 0.12), 0xc48a3f, 0.4, 0.6, 0.2);
  },
  cottage(g) {
    addMesh(g, G.box(2.5, 1.5, 2.5), 0xe0d6c0, 0, 0.75, 0);
    const roof = addMesh(g, G.cyl(0.01, 2.0, 2.8, 4), 0x5b6f82, 0, 2.15, 0); roof.rotation.y = Math.PI / 4; roof.scale.set(1, 0.6, 1);
    addMesh(g, G.box(0.55, 0.95, 0.06), 0x6a4a2e, 0, 0.48, 1.26);
    addMesh(g, G.box(0.45, 0.4, 0.06), 0xf3d483, 0.75, 0.95, 1.26, { emissive: 0x6a4a10 });
    addMesh(g, G.box(0.35, 0.9, 0.35), 0x8d8680, -0.7, 2.6, -0.5);
    // stone footing, timber corners, a framed door with a handle and step, a shuttered window
    addMesh(g, G.round(2.6, 0.22, 2.6, 0.06), 0x9a948a, 0, 0.1, 0);
    for (const [x, z] of [[-1.24, -1.24], [1.24, -1.24], [-1.24, 1.24], [1.24, 1.24]]) addMesh(g, G.round(0.14, 1.5, 0.14, 0.03), 0x6b4a2b, x, 0.8, z);
    addMesh(g, G.round(2.56, 0.1, 0.1, 0.03), 0x6b4a2b, 0, 1.5, 1.25);
    addMesh(g, G.round(0.7, 1.08, 0.05, 0.02), 0x5a3d24, 0, 0.54, 1.25);
    addMesh(g, G.sphere(0.035, 8), 0xd8b050, 0.18, 0.5, 1.3);
    addMesh(g, G.round(0.8, 0.08, 0.3, 0.03), 0x8a847a, 0, 0.04, 1.45);
    addMesh(g, G.round(0.56, 0.5, 0.05, 0.02), 0x6b4a2b, 0.75, 0.95, 1.255);
    for (const x of [0.4, 1.1]) addMesh(g, G.round(0.2, 0.46, 0.04, 0.015), 0x4a6a4a, x, 0.95, 1.3);
    // a flower box under the window
    addMesh(g, G.box(0.56, 0.12, 0.14), 0x7a5634, 0.75, 0.68, 1.33);
    for (let i = 0; i < 5; i++) addMesh(g, G.oct(0.05), [0xe85a5a, 0xf2d24a, 0xf4f0e6, 0xd87ad8, 0xf08a3a][i], 0.55 + i * 0.1, 0.79 + (i % 2) * 0.03, 1.33);
    for (let i = 0; i < 4; i++) addMesh(g, G.box(0.04, 0.08, 0.04), 0x4a8a3a, 0.6 + i * 0.1, 0.75, 1.36);
  },
  well(g) { addMesh(g, G.cyl(0.55, 0.6, 0.6, 10), 0x9a968f, 0, 0.3, 0); addMesh(g, G.cyl(0.42, 0.42, 0.05, 10), 0x2c4c5a, 0, 0.58, 0); for (const x of [-0.5, 0.5]) addMesh(g, G.box(0.08, 1.3, 0.08), 0x7a5634, x, 0.9, 0); const r = addMesh(g, G.cone(0.85, 0.5, 4), 0x5b6f82, 0, 1.75, 0); r.rotation.y = Math.PI / 4; },
  stones(g) { for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2 + 0.4; const s = addMesh(g, G.box(0.4, 1.9 - i * 0.15, 0.32), 0x9a968e, Math.cos(a) * 1.05, 0.85, Math.sin(a) * 1.05); s.rotation.set(0.25, -a, 0.12); } },
  tower(g) { addMesh(g, G.cyl(0.65, 0.9, 4.2, 7), 0xa8a397, 0, 2.1, 0); addMesh(g, G.cyl(0.85, 0.75, 0.3, 7), 0x8c877d, 0, 4.3, 0); addMesh(g, G.box(0.3, 0.5, 0.06), 0x2a2a2a, 0, 3.2, 0.8); addMesh(g, G.dod(0.32), 0xeee8d6, 0.2, 4.6, 0.1); },
  shrine(g) { addMesh(g, G.box(0.6, 0.7, 0.5), 0x9a968e, 0, 0.35, 0); for (const x of [-0.45, 0.45]) addMesh(g, G.box(0.08, 1.4, 0.08), 0x7a5634, x, 0.7, 0.3); const r = addMesh(g, G.cone(0.75, 0.45, 4), 0x6a4a2e, 0, 1.6, 0.1); r.rotation.y = Math.PI / 4; addMesh(g, G.dod(0.08), 0x9a6a3a, 0.12, 0.76, 0.1); addMesh(g, G.box(0.04, 0.3, 0.02), 0xc8553d, -0.15, 0.75, 0.26); },
  milestone(g) { addMesh(g, G.box(0.45, 1.0, 0.25), 0xa6a298, 0, 0.5, 0); const top = addMesh(g, G.cyl(0.23, 0.23, 0.25, 8), 0xa6a298, 0, 1.0, 0); top.rotation.x = Math.PI / 2; },
  wreck(g) { for (let i = 0; i < 6; i++) { const len = 1.2 - Math.abs(i - 2.5) * 0.15; const r = addMesh(g, G.box(0.1, len, 0.1), 0x6a5038, -1.2 + i * 0.48, 0.45, 0.35); r.rotation.x = -0.45; const r2 = addMesh(g, G.box(0.1, len, 0.1), 0x6a5038, -1.2 + i * 0.48, 0.45, -0.35); r2.rotation.x = 0.45; } addMesh(g, G.box(2.8, 0.14, 0.18), 0x5a4430, 0, 0.05, 0); g.rotation.y = 0.5; },
  mine(g) { addMesh(g, G.box(0.25, 1.8, 0.25), 0x6a5038, -0.85, 0.9, 0); addMesh(g, G.box(0.25, 1.8, 0.25), 0x6a5038, 0.85, 0.9, 0); addMesh(g, G.box(2.1, 0.28, 0.32), 0x6a5038, 0, 1.85, 0); addMesh(g, G.box(1.5, 1.7, 0.1), 0x0d0c0b, 0, 0.85, -0.12); addMesh(g, G.box(1.0, 0.05, 1.5), 0x8a4428, 0, 0.03, 0.7); addMesh(g, G.box(0.5, 0.4, 0.6), 0x5a4430, 0.9, 0.2, 1.1); },
  bell(g) { addMesh(g, G.box(0.14, 2.0, 0.14), 0x4a3f30, -0.7, 1.0, 0); addMesh(g, G.box(0.14, 2.0, 0.14), 0x4a3f30, 0.7, 1.0, 0); addMesh(g, G.box(1.6, 0.14, 0.18), 0x4a3f30, 0, 1.95, 0); const b = addMesh(g, G.cyl(0.25, 0.48, 0.65, 10), 0x9f7832, 0, 1.45, 0); b.rotation.z = 0.12; addMesh(g, G.dod(0.08), 0x9f7832, 0.06, 1.05, 0); },
  beacon(g) { addMesh(g, G.cyl(0.42, 0.6, 2.6, 6), 0x6d6a72, 0, 1.3, 0); addMesh(g, G.cyl(0.6, 0.4, 0.35, 6), 0x48484e, 0, 2.75, 0); const f = addMesh(g, G.cone(0.38, 0.8, 5), 0xffb347, 0, 3.25, 0, { emissive: 0xff7a10 }); f.castShadow = false; flames.push(f); },
  heart(g) { addMesh(g, G.cyl(0.6, 1.0, 4.5, 6), 0x363b47, 0, 2.25, 0); addMesh(g, G.oct(2.6), 0x2f8a8a, 0, 6.0, 0).scale.set(1, 1.3, 1); addMesh(g, G.oct(1.7), 0x42a6a0, 0.6, 8.2, 0.3); addMesh(g, G.oct(1.4), 0x2f8a8a, -1.6, 5.2, 0.8); },
  lantern(g) { addMesh(g, G.box(0.14, 2.2, 0.14), 0x4a3f30, 0, 1.1, 0); addMesh(g, G.box(0.6, 0.1, 0.1), 0x4a3f30, 0.25, 2.15, 0); const l = addMesh(g, G.box(0.26, 0.34, 0.26), 0xfff0b0, 0.5, 1.88, 0, { emissive: 0xffd070 }); l.castShadow = false; },
};

/* ---------------- characters ---------------- */
/**
 * A character's joints. `upper` pivots at the waist (lean, twist), `head` at the neck; the arms
 * hang from the upper body so a bend carries them along. `body` and `face` are the frames that
 * armour and hats attach to (they keep the old absolute coordinates). `tool` is the right hand,
 * `toolL` the left (bows, tongs).
 */
export interface CharacterModel {
  root: THREE.Group;
  upper: THREE.Group; body: THREE.Group; head: THREE.Group; face: THREE.Group;
  legL: THREE.Group; legR: THREE.Group; armL: THREE.Group; armR: THREE.Group; tool: THREE.Group; toolL: THREE.Group;
  /** The hat or hood: hidden under a helm or coif. */
  hat?: THREE.Group;
}
// wool and linen in earthy, dyed-once colours
const TUNICS = [0x5a6a7a, 0x7a3a30, 0x4f6a3a, 0x5a4a6a, 0x8a7a4a, 0x3e4e5e, 0x6a4a3a, 0x4a6a62, 0x7a6a5a, 0x5a3e2e];
const shadeHex = (c: number, k: number) => new THREE.Color(c).multiplyScalar(k).getHex();
/** A rounded box tapered toward its bottom (k = width kept at the bottom) and slightly toward its back. */
function tapered(w: number, h: number, d: number, r: number, k: number, kd = 0.94) {
  const g = G.round(w, h, d, r), p = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) { const t = (p.getY(i) + h / 2) / h, s = k + (1 - k) * Math.min(1, t * 1.35); p.setX(i, p.getX(i) * s); p.setZ(i, p.getZ(i) * (kd + (1 - kd) * t)); }
  g.computeVertexNormals(); return g;
}
export interface OutfitLike { tunic?: number; trousers?: number; hat?: number | null; hair?: number; skin?: number; apron?: number; scale?: number; staff?: boolean; hood?: boolean; beard?: boolean; fur?: number | null }
/**
 * A person, Valheim-ish: real proportions (long legs, a head about a seventh of the height on
 * a visible neck), broad shoulders over a tapered wool tunic with a skirted hem, a belt with a
 * pouch, wrapped shins and wrists, turned-down boots. Beards, long hair and braids, fur
 * mantles, all picked from the seed unless the outfit says. Joints: hips 0.72, waist 0.78,
 * shoulders 1.18, neck 1.26; the whole figure stands about 1.52 tall.
 */
export function buildCharacter(seed: number, o: OutfitLike = {}): CharacterModel {
  // mix the seed so nearby numbers still look unrelated, then take different bits for each choice
  let S = Math.imul(Math.abs(seed) ^ 0x9e3779b9, 2654435761) >>> 0; S = (S ^ (S >>> 15)) >>> 0; S = Math.imul(S, 2246822519) >>> 1;
  const pick = <T,>(arr: T[], shift: number) => arr[(S >> shift) % arr.length];
  const tunic = o.tunic ?? TUNICS[S % TUNICS.length];
  const root = new THREE.Group();
  // waist joint, then a frame that keeps absolute coordinates for everything above it
  const upper = new THREE.Group(); upper.position.set(0, 0.78, 0); root.add(upper);
  const body = new THREE.Group(); body.position.y = -0.78; upper.add(body);
  const head = new THREE.Group(); head.position.set(0, 1.26, 0); body.add(head);
  const face = new THREE.Group(); face.position.y = -1.26; head.add(face);
  const limb = (parent: THREE.Object3D, x: number, y: number) => { const g = new THREE.Group(); g.position.set(x, y, 0); parent.add(g); return g; };
  const legL = limb(root, -0.095, 0.72), legR = limb(root, 0.095, 0.72);
  const trousers = o.trousers ?? pick([0x3a3328, 0x2f3438, 0x4a3a2a, 0x3a3e30, 0x2a2622], 3);
  const hat = o.hat === undefined ? (S >> 5) % 3 === 0 ? pick([0x5a4632, 0x3e342a, 0x6a5a3a, 0x3a3a38], 6) : null : o.hat;
  const hair = o.hair ?? pick([0x3a2a1a, 0x6a3a1e, 0xb08a52, 0x1e1a18, 0x8a4a22, 0x5a4a3a], 7);
  const skin = o.skin ?? pick([0xd8a882, 0xc08a64, 0x8a5e40, 0xe8c09a, 0xb07a58], 9);
  const beard = o.beard ?? (S >> 11) % 5 < 3;
  const fur = o.fur === undefined ? ((S >> 13) % 4 === 0 ? pick([0x8a7a62, 0x5a4a3a, 0xb8a888, 0x6a6460], 15) : null) : o.fur;
  const longHair = (S >> 17) % 3 === 0, braids = (S >> 19) % 4 === 0;
  const tunicD = shadeHex(tunic, 0.78), leather = pick([0x5a3e26, 0x4a3420, 0x6a4a2e], 21), leatherD = shadeHex(leather, 0.7), wrap = pick([0x8a7a62, 0x6a5e4c, 0x5a5048], 23);
  const skinD = shadeHex(skin, 0.86);

  // legs: thigh and shin, wrapped shins with leather ties, turned-down boots with a sole
  for (const leg of [legL, legR]) {
    addMesh(leg, G.capsule(0.082, 0.24), trousers, 0, -0.17, 0);
    addMesh(leg, G.capsule(0.066, 0.2), wrap, 0, -0.45, 0.005);
    for (const y of [-0.38, -0.46, -0.54]) addMesh(leg, G.cyl(0.071, 0.071, 0.014, 10), leatherD, 0, y, 0.005).rotation.z = 0.15;
    addMesh(leg, G.round(0.13, 0.13, 0.22, 0.05), leather, 0, -0.645, 0.025);
    addMesh(leg, G.round(0.145, 0.04, 0.17, 0.02), leatherD, 0, -0.585, 0.01);
    addMesh(leg, G.round(0.14, 0.025, 0.24, 0.01), 0x1a140e, 0, -0.71, 0.03);
  }
  // hips, the torso (broad at the chest, in at the waist), a skirted hem below the belt
  addMesh(body, G.round(0.34, 0.14, 0.22, 0.06), trousers, 0, 0.77, 0);
  addMesh(body, tapered(0.47, 0.44, 0.27, 0.11, 0.8), tunic, 0, 1.0, 0.005);
  addMesh(body, tapered(0.41, 0.16, 0.26, 0.05, 1.12, 1), tunic, 0, 0.73, 0);
  addMesh(body, G.round(0.42, 0.025, 0.27, 0.01), tunicD, 0, 0.655, 0);
  for (const sx of [-1, 1]) addMesh(body, G.sphere(0.088, 12), tunic, sx * 0.215, 1.16, 0).scale.set(1, 0.85, 1);
  // collar, neck
  addMesh(body, G.round(0.2, 0.05, 0.17, 0.025), tunicD, 0, 1.225, 0.005);
  addMesh(body, G.cyl(0.055, 0.065, 0.1, 10), skin, 0, 1.27, 0);
  if (o.apron !== undefined) addMesh(body, tapered(0.34, 0.56, 0.03, 0.015, 1.1, 1), o.apron, 0, 0.84, 0.14);
  // the belt: a buckle, a pouch on the hip, and a knife sheath on the other
  addMesh(body, G.round(0.42, 0.06, 0.28, 0.025), leather, 0, 0.82, 0);
  addMesh(body, G.round(0.07, 0.06, 0.03, 0.01), 0xb09050, 0, 0.82, 0.14);
  addMesh(body, G.round(0.1, 0.1, 0.06, 0.03), leatherD, 0.2, 0.76, 0.06);
  addMesh(body, G.round(0.04, 0.16, 0.03, 0.012), 0x2a2018, -0.2, 0.74, 0.05).rotation.z = 0.2;
  if (o.apron === undefined && !fur) {
    // a cross strap over the chest to a small pack
    const st = addMesh(body, G.round(0.05, 0.56, 0.02, 0.01), leatherD, 0, 1.0, 0.14); st.rotation.z = 0.62;
    addMesh(body, G.round(0.28, 0.28, 0.12, 0.05), leather, 0, 1.0, -0.19);
    addMesh(body, G.round(0.3, 0.06, 0.13, 0.02), leatherD, 0, 1.13, -0.19);
  }
  if (fur) {
    // a fur mantle round the shoulders and a short cloak down the back
    addMesh(body, G.round(0.52, 0.1, 0.32, 0.05), fur, 0, 1.19, -0.01);
    for (let i = 0; i < 7; i++) addMesh(body, G.cone(0.03, 0.06, 4), shadeHex(fur, 0.9), -0.21 + i * 0.07, 1.13, 0.15).rotation.x = Math.PI;
    addMesh(body, tapered(0.44, 0.52, 0.03, 0.015, 1.15, 1), shadeHex(fur, 0.6), 0, 0.92, -0.16);
  }

  // head: skull, jaw and cheekbones, brow and nose; small eyes set under the brow
  addMesh(face, G.round(0.2, 0.23, 0.215, 0.085), skin, 0, 1.4, 0);
  addMesh(face, G.round(0.165, 0.09, 0.17, 0.05), skin, 0, 1.31, 0.012);
  addMesh(face, G.round(0.17, 0.028, 0.05, 0.012), skinD, 0, 1.425, 0.085);
  { const n = addMesh(face, G.round(0.036, 0.07, 0.05, 0.016), skinD, 0, 1.385, 0.105); n.rotation.x = -0.15; }
  for (const x of [-0.042, 0.042]) {
    addMesh(face, G.sphere(0.012, 8), 0x14100d, x, 1.402, 0.1);
    addMesh(face, G.round(0.05, 0.013, 0.02, 0.006), hair, x, 1.436, 0.101).rotation.z = x > 0 ? -0.12 : 0.12;
    addMesh(face, G.round(0.03, 0.055, 0.04, 0.012), skinD, x * 2.45, 1.39, -0.005);
  }
  if (beard) {
    // a full beard and moustache, a little shorter or longer by seed
    const len = 0.1 + ((S >> 25) % 3) * 0.04;
    addMesh(face, tapered(0.17, len, 0.11, 0.04, 0.55, 1), hair, 0, 1.3 - len / 2 + 0.04, 0.06);
    addMesh(face, G.round(0.09, 0.022, 0.03, 0.01), hair, 0, 1.352, 0.102);
    for (const x of [-0.088, 0.088]) addMesh(face, G.round(0.025, 0.09, 0.12, 0.01), hair, x, 1.34, 0.025);
  } else addMesh(face, G.round(0.05, 0.008, 0.01, 0.004), 0x7a4a3a, 0, 1.335, 0.098);
  // hair: a cap over the crown and back, longer by seed, braids at the temples
  addMesh(face, G.round(0.215, 0.1, 0.225, 0.05), hair, 0, 1.475, -0.008);
  addMesh(face, G.round(0.2, longHair ? 0.3 : 0.16, 0.07, 0.03), hair, 0, longHair ? 1.33 : 1.4, -0.1);
  if (braids) for (const x of [-0.1, 0.1]) { addMesh(face, G.capsule(0.018, 0.16), hair, x, 1.28, 0.03); addMesh(face, G.cyl(0.02, 0.02, 0.02, 6), 0xb09050, x, 1.2, 0.03); }
  // the hat (or hood) in a group of its own, so a helm or coif can hide it after the rig is merged
  const hatG = new THREE.Group(); face.add(hatG);
  if (o.hood) { addMesh(hatG, G.round(0.27, 0.17, 0.27, 0.07), tunic, 0, 1.49, -0.015); addMesh(hatG, G.round(0.27, 0.25, 0.06, 0.025), tunic, 0, 1.36, -0.13); addMesh(hatG, G.round(0.34, 0.07, 0.28, 0.03), tunicD, 0, 1.24, -0.02); }
  else if (hat !== null) {
    addMesh(hatG, G.cyl(0.24, 0.25, 0.03, 16), hat, 0, 1.5, 0);
    addMesh(hatG, G.cyl(0.11, 0.13, 0.14, 12), hat, 0, 1.58, 0);
    addMesh(hatG, G.cyl(0.133, 0.133, 0.03, 12), leatherD, 0, 1.525, 0);
  }
  // arms: upper arm and forearm, a wrapped wrist, a hand
  const armL = limb(body, -0.255, 1.17), armR = limb(body, 0.255, 1.17);
  for (const arm of [armL, armR]) {
    addMesh(arm, G.capsule(0.064, 0.2), tunic, 0, -0.14, 0);
    addMesh(arm, G.capsule(0.054, 0.18), tunicD, 0, -0.36, 0.005);
    addMesh(arm, G.cyl(0.058, 0.055, 0.07, 10), wrap, 0, -0.44, 0.005);
    addMesh(arm, G.round(0.065, 0.1, 0.075, 0.03), skin, 0, -0.53, 0.01);
  }
  if (o.staff) { const st = addMesh(armL, G.cyl(0.03, 0.035, 1.6, 5), 0x6b4a2b, 0, -0.2, 0.08); st.rotation.x = 0.1; }
  const tool = new THREE.Group(); tool.position.set(0, -0.54, 0.05); armR.add(tool);
  const toolL = new THREE.Group(); toolL.position.set(0, -0.54, 0.05); armL.add(toolL);
  if (o.scale) root.scale.setScalar(o.scale);
  mergeRig(root, [tool, toolL]);
  return { root, upper, body, head, face, hat: hatG, legL, legR, armL, armR, tool, toolL };
}
/** Colour by metal: 0 worn stone, 1 bronze, 2 iron, 3 starsilver, 4 heartwood, 5 wood, 6 steel, 7 deepglass, 8 emberheart, 9 silver, 10 gold, 11 cobalt, 12 titanium, 13 tungsten, 14 black iron, 15 iridium. */
export const TOOL_COL = [0x8a7a68, 0xc48a3f, 0xa3aab1, 0xbff0ff, 0x5a7a3a, 0x8a6a3e, 0x7d8894, 0x5ad0e8, 0xe8602a, 0xdde2e8, 0xf0c040, 0x3a5ad8, 0xc8ccd2, 0x6a6a72, 0x2a2a30, 0xc49cff, 0x8ab8b0, 0xeef0f6, 0x9a6a3e, 0xb89a74, 0x8a5a32, 0x7a7a80, 0x5e4028, 0x4a6a4e, 0x7a6450, 0xc8cce8, 0xdce8ff];
export function setHeldTool(m: CharacterModel, kind: string | null, tier = 0, icon?: { color?: string }) {
  while (m.tool.children.length) { const c = m.tool.children[0]; m.tool.remove(c); freeTree(c); }
  if (kind === 'axe') { addMesh(m.tool, G.box(0.05, 0.05, 0.6), 0x6b4a2b, 0, 0, 0.25); addMesh(m.tool, G.box(0.04, 0.2, 0.16), TOOL_COL[tier], 0, 0.06, 0.5); }
  if (kind === 'pick') { addMesh(m.tool, G.box(0.05, 0.05, 0.6), 0x6b4a2b, 0, 0, 0.25); addMesh(m.tool, G.box(0.04, 0.5, 0.07), TOOL_COL[tier], 0, 0, 0.52); }
  if (kind === 'rod') {
    const r = addMesh(m.tool, G.cyl(0.015, 0.03, 1.6, 4), 0x9b7b4a, 0, 0.2, 0.7); r.rotation.x = Math.PI / 2 - 0.5;
    const tip = new THREE.Object3D(); tip.name = 'tip'; tip.position.set(0, 0.8, 0); r.add(tip);
  }
  if (kind === 'net') {
    const h = addMesh(m.tool, G.cyl(0.02, 0.025, 1.0, 4), 0x8a6a3e, 0, 0.1, 0.45); h.rotation.x = Math.PI / 2 - 0.4;
    const ring = addMesh(m.tool, G.box(0.36, 0.03, 0.3), 0xd8c9a8, 0, 0.36, 0.95); ring.rotation.x = -0.4;
  }
  if (kind === 'harpoon') {
    const h = addMesh(m.tool, G.cyl(0.02, 0.025, 1.5, 4), 0x6b4a2b, 0, 0.12, 0.6); h.rotation.x = Math.PI / 2 - 0.25;
    const tip = addMesh(m.tool, G.box(0.05, 0.05, 0.22), TOOL_COL[tier], 0, 0.3, 1.36); tip.rotation.x = -0.25;
  }
  if (kind === 'trap') { addMesh(m.tool, G.box(0.26, 0.2, 0.34), 0xa07a44, 0, -0.08, 0.12); addMesh(m.tool, G.box(0.2, 0.04, 0.3), 0x6b4a2b, 0, 0.04, 0.12); }
  if (kind === 'skewer') { addMesh(m.tool, G.box(0.02, 0.02, 0.7), 0x8a6a3e, 0, 0, 0.3); addMesh(m.tool, G.dod(0.06), 0xb0603a, 0, 0, 0.52); addMesh(m.tool, G.dod(0.05), 0xc07a4a, 0, 0, 0.42); }
  if (kind === 'knife') { addMesh(m.tool, G.box(0.03, 0.03, 0.1), 0x5a3a20, 0, 0, 0.04); addMesh(m.tool, G.box(0.01, 0.04, 0.16), 0xc8ccd0, 0, 0, 0.16); }
  if (kind === 'food') { addMesh(m.tool, G.box(0.12, 0.08, 0.1), 0xc8964a, 0, 0, 0.04); }
  if (kind === 'hoe') {
    const h = addMesh(m.tool, G.cyl(0.022, 0.026, 1.3, 5), 0x8a6a3e, 0, 0.05, 0.5); h.rotation.x = Math.PI / 2;
    addMesh(m.tool, G.box(0.2, 0.03, 0.12), TOOL_COL[tier] ?? 0x6a6e72, 0, -0.05, 1.12).rotation.x = 0.3;
    addMesh(m.tool, G.box(0.03, 0.1, 0.03), TOOL_COL[tier] ?? 0x6a6e72, 0, 0.0, 1.1);
  }
  if (kind === 'can') {
    addMesh(m.tool, G.cyl(0.11, 0.12, 0.22, 10), 0x5a8a6a, 0, -0.12, 0.12);
    const sp = addMesh(m.tool, G.cyl(0.015, 0.025, 0.3, 6), 0x4a7a5a, 0, -0.05, 0.32); sp.rotation.x = Math.PI / 2 - 0.6;
    addMesh(m.tool, G.cyl(0.035, 0.02, 0.04, 8), 0x3a6a4a, 0, 0.05, 0.44).rotation.x = Math.PI / 2 - 0.6;
    const hd = addMesh(m.tool, G.box(0.02, 0.16, 0.02), 0x4a7a5a, 0, 0.04, 0.0); hd.rotation.x = 0.3;
  }
  if (kind === 'hammer') { addMesh(m.tool, G.box(0.05, 0.05, 0.4), 0x6b4a2b, 0, 0, 0.17); addMesh(m.tool, G.box(0.1, 0.18, 0.1), 0x4a4d53, 0, 0, 0.36); }
  // makeshift weapons: a log swung like a club, anything else held out like it means business
  if (kind === 'club') { const c = addMesh(m.tool, G.cyl(0.05, 0.08, 0.7, 7), new THREE.Color(icon?.color ?? '#7a5634').getHex(), 0, 0, 0.3); c.rotation.x = Math.PI / 2; }
  if (kind === 'item') { addMesh(m.tool, G.dod(0.1), new THREE.Color(icon?.color ?? '#b8a888').getHex(), 0, 0, 0.12); }
}

/** Village clutter (map `prop` objects). Signposts point toward +z; the map's `facing` turns them. */
BUILDERS.barrel = g => {
  addMesh(g, G.cyl(0.27, 0.24, 0.62, 9), 0x8a6238, 0, 0.31, 0);
  for (const y of [0.1, 0.52]) addMesh(g, G.cyl(0.285, 0.285, 0.04, 9), 0x3a3a38, 0, y, 0);
  addMesh(g, G.cyl(0.23, 0.23, 0.02, 9), 0x6b4a2b, 0, 0.62, 0);
  addMesh(g, G.cyl(0.2, 0.18, 0.46, 8), 0x7a5634, 0.42, 0.23, 0.18);
  addMesh(g, G.cyl(0.21, 0.21, 0.03, 8), 0x3a3a38, 0.42, 0.4, 0.18);
};
BUILDERS.crates = g => {
  addMesh(g, G.box(0.5, 0.46, 0.5), 0xa07a44, -0.1, 0.23, 0.05);
  addMesh(g, G.box(0.52, 0.05, 0.52), 0x7a5634, -0.1, 0.47, 0.05);
  const top = addMesh(g, G.box(0.38, 0.34, 0.38), 0xb08a52, -0.06, 0.66, 0.02); top.rotation.y = 0.4;
  addMesh(g, G.box(0.36, 0.3, 0.36), 0x9a7448, 0.33, 0.15, -0.22).rotation.y = -0.3;
};
BUILDERS.woodpile = g => {
  for (let i = 0; i < 7; i++) { const row = i < 4 ? 0 : 1, k = row ? i - 4 : i; const l = addMesh(g, G.cyl(0.1, 0.1, 0.8, 7), i % 2 ? 0x8a6238 : 0x7a5634, (k - (row ? 1 : 1.5)) * 0.2, 0.1 + row * 0.18, 0); l.rotation.x = Math.PI / 2; }
  for (const x of [-0.42, 0.42]) addMesh(g, G.box(0.05, 0.5, 0.05), 0x5a3d24, x, 0.25, 0);
  const axe = addMesh(g, G.box(0.04, 0.5, 0.04), 0x6b4a2b, 0.15, 0.52, 0.2); axe.rotation.z = 0.5;
  addMesh(g, G.box(0.16, 0.1, 0.04), 0xa3aab1, 0.27, 0.72, 0.2).rotation.z = 0.5;
};
BUILDERS.cart = g => {
  addMesh(g, G.box(0.85, 0.32, 0.6), 0x8a6238, 0, 0.5, 0);
  addMesh(g, G.box(0.9, 0.05, 0.65), 0x6b4a2b, 0, 0.34, 0);
  for (const x of [-0.38, 0.38]) { const w = addMesh(g, G.cyl(0.26, 0.26, 0.06, 10), 0x5a3d24, x, 0.26, 0.34); w.rotation.x = Math.PI / 2; const w2 = addMesh(g, G.cyl(0.26, 0.26, 0.06, 10), 0x5a3d24, x, 0.26, -0.34); w2.rotation.x = Math.PI / 2; }
  for (const z of [-0.12, 0.12]) { const s2 = addMesh(g, G.box(0.04, 0.04, 0.8), 0x6b4a2b, 0.75, 0.42, z); s2.rotation.y = Math.PI / 2; s2.position.x = 0.75; }
  addMesh(g, G.dod(0.16), 0xd8c8a0, -0.15, 0.75, 0.05).scale.set(1, 0.8, 1.1);
  addMesh(g, G.dod(0.14), 0xc8b88a, 0.15, 0.73, -0.08);
};
BUILDERS.signpost = g => {
  addMesh(g, G.box(0.09, 1.5, 0.09), 0x6b4a2b, 0, 0.75, 0);
  const board = addMesh(g, G.box(0.08, 0.24, 0.62), 0xb08a5a, 0, 1.28, 0.2);
  addMesh(g, G.cone(0.13, 0.18, 4), 0xb08a5a, 0, 1.28, 0.58).rotation.x = Math.PI / 2;
  for (const y of [1.22, 1.33]) addMesh(g, G.box(0.09, 0.02, 0.45), 0x5a3d24, 0, y, 0.2);
  void board;
};

/**
 * The old world, after the End: what's left by the roads and across the land. Rust, cracked
 * concrete and moss; nothing with a name on it.
 */
const RUST = [0x8a4a2a, 0x7a5236, 0x6e4a3a, 0x8e5a34];
BUILDERS.wreck_car = g => {
  const body = new THREE.Group(); g.add(body);
  const c = RUST[Math.floor(Math.random() * RUST.length)];
  // a sunk, flat-tyred saloon: chassis, cabin with the roof caved in, empty windows
  addMesh(body, G.round(1.9, 0.42, 0.86, 0.12), c, 0, 0.34, 0);
  addMesh(body, G.round(1.0, 0.36, 0.8, 0.1), c, -0.12, 0.72, 0).rotation.z = 0.06;
  addMesh(body, G.box(0.92, 0.06, 0.74), 0x5a3a2a, -0.12, 0.86, 0).rotation.z = 0.12; // the dented roof
  for (const z of [-0.41, 0.41]) addMesh(body, G.box(0.7, 0.2, 0.02), 0x1e1e20, -0.12, 0.72, z);
  addMesh(body, G.box(0.02, 0.22, 0.66), 0x1e1e20, 0.38, 0.72, 0);
  addMesh(body, G.box(0.06, 0.12, 0.7), 0x5a5a58, 0.96, 0.34, 0); // bumper
  for (const [x, z] of [[0.6, 0.44], [-0.62, 0.44], [0.6, -0.44]]) { const w = addMesh(body, G.cyl(0.19, 0.19, 0.12, 10), 0x26262a, x, 0.14, z); w.rotation.x = Math.PI / 2; }
  addMesh(body, G.box(0.24, 0.08, 0.08), 0x3a3a3c, -0.62, 0.06, -0.44); // a wheel long gone, the axle on the ground
  body.rotation.z = 0.05; body.position.y = -0.04;
  // grass and a bush grown up through it
  addMesh(g, G.ico(0.22), 0x5a7a3a, 0.7, 0.12, -0.5).scale.set(1.2, 0.6, 1);
  addMesh(g, G.ico(0.18), 0x6a8a42, -0.9, 0.1, 0.4).scale.set(1, 0.7, 1.2);
};
BUILDERS.pylon = g => {
  // a dead power pylon: four legs tapering to a waist, cross-arms, no wires
  const steel = 0x6a6660, tilt = new THREE.Group(); g.add(tilt);
  const H = 6.2, legs: [number, number][] = [[-0.7, -0.7], [0.7, -0.7], [0.7, 0.7], [-0.7, 0.7]];
  for (const [x, z] of legs) { const l = addMesh(tilt, G.box(0.08, H, 0.08), steel, x * 0.55, H / 2, z * 0.55); l.rotation.z = -x * 0.09; l.rotation.x = z * 0.09; }
  for (const y of [1.4, 2.8, 4.2]) { const w = 1.25 - y * 0.12; addMesh(tilt, G.box(w, 0.05, 0.05), steel, 0, y, -w / 2 + 0.02); addMesh(tilt, G.box(w, 0.05, 0.05), steel, 0, y, w / 2 - 0.02); addMesh(tilt, G.box(0.05, 0.05, w), steel, -w / 2 + 0.02, y, 0); addMesh(tilt, G.box(0.05, 0.05, w), steel, w / 2 - 0.02, y, 0); }
  for (let i = 0; i < 3; i++) { const b = addMesh(tilt, G.box(0.04, 1.6, 0.04), steel, 0, 0.7 + i * 1.4, 0.5 - i * 0.12); b.rotation.x = 0.8; }
  addMesh(tilt, G.box(2.6, 0.1, 0.12), steel, 0, H - 0.6, 0);
  addMesh(tilt, G.box(1.8, 0.1, 0.12), steel, 0, H - 0.05, 0);
  for (const x of [-1.2, 1.2, -0.8, 0.8]) addMesh(tilt, G.cyl(0.05, 0.05, 0.3, 6), 0x9a948a, x, Math.abs(x) > 1 ? H - 0.8 : H - 0.25, 0);
  addMesh(tilt, G.box(2.4, 0.03, 0.03), 0x2a2a2a, 0.3, H - 1.4, 0.05).rotation.z = -0.5; // one wire, hanging loose
  tilt.rotation.z = (Math.random() - 0.5) * 0.12; tilt.rotation.x = (Math.random() - 0.5) * 0.08;
  addMesh(g, G.round(1.4, 0.16, 1.4, 0.05), 0x8a8680, 0, 0.06, 0); // the footing
};
BUILDERS.ruin_wall = g => {
  // a stretch of broken wall: blocks, a jagged top, rebar, a window gap, moss
  const C = [0x9a948c, 0x8a857e, 0xa49c90];
  for (let i = 0; i < 6; i++) {
    const h = 0.4 + Math.abs(Math.sin(i * 1.7 + 0.4)) * 1.1 + (i === 2 || i === 3 ? 0.6 : 0);
    if (i === 3) { addMesh(g, G.box(0.36, 0.5, 0.28), C[i % 3], -0.9 + i * 0.36, 0.25, 0); addMesh(g, G.box(0.36, 0.3, 0.28), C[(i + 1) % 3], -0.9 + i * 0.36, h - 0.15, 0); continue; }
    addMesh(g, G.box(0.36, h, 0.28), C[i % 3], -0.9 + i * 0.36, h / 2, 0).rotation.z = (Math.sin(i * 3.1) * 0.03);
  }
  for (const x of [-0.6, 0.4, 0.7]) addMesh(g, G.box(0.03, 0.5, 0.03), 0x6a3a22, x, 1.35 + Math.abs(x) * 0.2, 0).rotation.z = x * 0.6;
  for (let i = 0; i < 4; i++) addMesh(g, G.dod(0.12 + (i % 2) * 0.06), C[i % 3], -1 + i * 0.6, 0.06, 0.35 + (i % 2) * 0.2);
  addMesh(g, G.ico(0.2), 0x4a6a32, -0.7, 0.2, 0.16).scale.set(1.4, 0.5, 0.6);
  addMesh(g, G.ico(0.16), 0x5a7a3a, 0.8, 0.5, 0.15).scale.set(1, 0.6, 0.5);
};
BUILDERS.brazier = g => {
  for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2, l = addMesh(g, G.box(0.05, 0.9, 0.05), 0x3a3634, Math.cos(a) * 0.22, 0.45, Math.sin(a) * 0.22); l.rotation.x = Math.sin(a) * 0.2; l.rotation.z = -Math.cos(a) * 0.2; }
  addMesh(g, G.cyl(0.36, 0.22, 0.28, 10), 0x4a4440, 0, 0.98, 0);
  addMesh(g, G.dod(0.14), 0x2a2220, 0, 1.08, 0);
  const f1 = addMesh(g, G.cone(0.24, 0.6, 5), 0xffb347, 0, 1.38, 0, { emissive: 0xff8a1a }); f1.castShadow = false;
  const f2 = addMesh(g, G.cone(0.13, 0.42, 5), 0xfff0a0, 0, 1.32, 0, { emissive: 0xffd060 }); f2.castShadow = false;
  flames.push(f1, f2);
};
BUILDERS.minecart = g => {
  for (const z of [-0.28, 0.28]) addMesh(g, G.box(1.8, 0.05, 0.06), 0x5a4a40, 0, 0.03, z);
  for (let i = 0; i < 6; i++) addMesh(g, G.box(0.12, 0.04, 0.8), 0x5a3d24, -0.8 + i * 0.32, 0.01, 0);
  addMesh(g, G.round(0.9, 0.5, 0.62, 0.06), 0x6a4a34, 0.1, 0.45, 0).rotation.z = 0.08;
  addMesh(g, G.box(0.82, 0.06, 0.54), 0x2a2a2c, 0.1, 0.71, 0);
  for (const [x, z] of [[-0.2, -0.3], [0.4, -0.3], [-0.2, 0.3], [0.4, 0.3]]) { const w = addMesh(g, G.cyl(0.11, 0.11, 0.06, 8), 0x2a2a2c, x, 0.14, z); w.rotation.x = Math.PI / 2; }
  addMesh(g, G.dod(0.18), 0x4a4440, 0.0, 0.8, 0.05); addMesh(g, G.dod(0.14), 0x5a5048, 0.25, 0.78, -0.08);
};

/** Looks for the new stations. */
BUILDERS.range = g => {
  addMesh(g, G.box(1.0, 0.5, 0.9), 0x9a6a4a, 0, 0.25, 0);
  addMesh(g, G.dod(0.55), 0xb07a52, 0, 0.75, 0).scale.set(1, 0.8, 0.9);
  addMesh(g, G.box(0.36, 0.3, 0.1), 0x2a1a12, 0, 0.62, 0.42);
  addMesh(g, G.cyl(0.08, 0.1, 0.5, 6), 0x8a5a3a, 0.2, 1.25, -0.1);
};
BUILDERS.workbench = g => {
  addMesh(g, G.box(1.1, 0.1, 0.6), 0x8a6a3e, 0, 0.62, 0);
  for (const [x, z] of [[-0.48, -0.24], [0.48, -0.24], [-0.48, 0.24], [0.48, 0.24]]) addMesh(g, G.box(0.08, 0.6, 0.08), 0x6b4a2b, x, 0.3, z);
  const bow = new THREE.Mesh(new THREE.TorusGeometry(0.4, 0.025, 4, 12, Math.PI * 0.8), mat(0x8a5a32)); bow.rotation.set(-Math.PI / 2, 0, 0.3); bow.position.set(-0.1, 0.7, 0); g.add(bow);
  for (let i = 0; i < 5; i++) addMesh(g, G.box(0.02, 0.02, 0.5), 0xd8c098, 0.3 + i * 0.03, 0.69, -0.05);
};

/* ---------------- getting around, and what the survivors built from scrap ---------------- */
BUILDERS.waystone = g => {
  // a standing stone from before the End, carved since by the Drift: notches, a band of paint, a lamp hook
  const s = addMesh(g, G.round(0.62, 2.3, 0.42, 0.12), 0x8e9894, 0, 1.15, 0); s.rotation.y = 0.2; s.rotation.z = 0.04;
  addMesh(g, G.round(0.66, 0.16, 0.46, 0.05), 0x2a8aa0, 0, 1.55, 0).rotation.y = 0.2;
  for (let i = 0; i < 4; i++) addMesh(g, G.box(0.36, 0.04, 0.02), 0x3a4a48, -0.02, 0.6 + i * 0.18, 0.22).rotation.y = 0.2;
  const glyph = addMesh(g, G.oct(0.12), 0x9ef0ff, 0.02, 1.98, 0.2, { emissive: 0x2aa6c8 }); glyph.castShadow = false;
  for (let i = 0; i < 5; i++) { const a = i * 1.3; addMesh(g, G.dod(0.14 + (i % 2) * 0.05), 0x7a827e, Math.cos(a) * 0.55, 0.06, Math.sin(a) * 0.5); }
  addMesh(g, G.ico(0.18), 0x4a6a32, 0.3, 0.08, -0.35).scale.set(1.3, 0.5, 1);
};
BUILDERS.cache = g => {
  // a lifeboat on its side in the shingle, a lashed crate beside it
  const hull = new THREE.Group(); g.add(hull); hull.rotation.z = 1.1; hull.position.set(-0.2, 0.42, 0);
  addMesh(hull, G.round(2.2, 0.32, 0.9, 0.14), 0xd86a2a, 0, 0, 0);
  addMesh(hull, G.round(2.0, 0.3, 0.7, 0.1), 0xe8e0d0, 0, 0.12, 0);
  addMesh(hull, G.box(0.1, 0.34, 0.92), 0x5a4430, 0.9, 0.05, 0);
  addMesh(g, G.round(0.6, 0.48, 0.5, 0.04), 0x8a6a3e, 0.9, 0.24, 0.3).rotation.y = 0.4;
  addMesh(g, G.box(0.64, 0.05, 0.54), 0x3a2a1e, 0.9, 0.36, 0.3).rotation.y = 0.4;
  addMesh(g, G.cyl(0.03, 0.03, 1.4, 6), 0xc8b890, 0.3, 0.06, 0.6).rotation.z = Math.PI / 2;
};
BUILDERS.ferry = g => {
  // Tobin's ferry: a flat old punt with a rail and a pole, afloat off the end of a short jetty
  // (the station tile is the jetty foot; the boat sits out over the water, +z)
  for (let i = 0; i < 4; i++) addMesh(g, G.box(1.0, 0.08, 0.32), 0x7a6040, 0, 0.12, 0.1 + i * 0.36);
  for (const x of [-0.45, 0.45]) addMesh(g, G.cyl(0.07, 0.08, 1.2, 8), 0x5a4430, x, -0.2, 1.3);
  const boat = new THREE.Group(); boat.position.set(0, -0.32, 2.6); g.add(boat);
  addMesh(boat, G.round(1.2, 0.3, 2.6, 0.08), 0x6a5038, 0, 0.05, 0);
  addMesh(boat, G.box(1.1, 0.04, 2.5), 0x8a6a48, 0, 0.21, 0);
  for (const x of [-0.55, 0.55]) addMesh(boat, G.box(0.06, 0.06, 2.4), 0x4a3a2a, x, 0.6, 0);
  for (const z of [-1.1, 0, 1.1]) for (const x of [-0.55, 0.55]) addMesh(boat, G.box(0.06, 0.4, 0.06), 0x4a3a2a, x, 0.4, z);
  const pole = addMesh(boat, G.cyl(0.03, 0.03, 3, 6), 0x9a7a52, 0.3, 1.0, 0.6); pole.rotation.x = 0.9;
  addMesh(boat, G.box(0.4, 0.36, 0.5), 0x2f6f8f, -0.2, 0.4, -0.6);
};
const CORRUGATED = (w: number, d: number, color: number) => {
  const g = new THREE.Group();
  addMesh(g, G.box(w, 0.03, d), color, 0, 0, 0);
  for (let i = 0; i < Math.floor(w / 0.12); i++) addMesh(g, G.box(0.03, 0.035, d), new THREE.Color(color).multiplyScalar(0.8).getHex(), -w / 2 + 0.06 + i * 0.12, 0.02, 0);
  return g;
};
BUILDERS.container = g => {
  // a shipping container, door end open, made into a store: ribbed walls, rust bloom, a tarp over the gap
  const c = [0x3a6a7a, 0x8a3a2a, 0x6a7a3a][Math.floor(Math.random() * 3)];
  addMesh(g, G.box(3.0, 1.3, 1.2), c, 0, 0.65, 0);
  for (let i = 0; i < 12; i++) for (const z of [-0.61, 0.61]) addMesh(g, G.box(0.08, 1.2, 0.03), new THREE.Color(c).multiplyScalar(0.78).getHex(), -1.38 + i * 0.25, 0.65, z);
  addMesh(g, G.box(0.04, 1.3, 1.2), 0x1e1e20, 1.5, 0.65, 0);
  addMesh(g, G.box(0.05, 1.1, 0.9), 0xb8a070, 1.53, 0.6, 0).rotation.z = 0.04;
  addMesh(g, G.box(1.0, 0.5, 0.04), 0x7a4a2a, -0.6, 0.4, 0.63);
  addMesh(g, G.box(3.0, 0.06, 1.24), new THREE.Color(c).multiplyScalar(0.7).getHex(), 0, 1.32, 0);
  addMesh(g, G.dod(0.15), 0x7a7268, 0.8, 1.42, -0.3); addMesh(g, G.ico(0.2), 0x4a6a32, -1.2, 1.36, 0.2).scale.set(1.4, 0.4, 1);
};
BUILDERS.billboard = g => {
  // a roadside billboard, the advert long bleached away; Meadowfold has painted its own words over it
  for (const x of [-1.2, 1.2]) addMesh(g, G.box(0.14, 3.2, 0.14), 0x5a5650, x, 1.6, 0);
  addMesh(g, G.box(3.2, 1.5, 0.1), 0xd8d0b8, 0, 3.0, 0);
  addMesh(g, G.box(3.3, 0.08, 0.14), 0x4a4640, 0, 2.22, 0);
  addMesh(g, G.box(3.3, 0.08, 0.14), 0x4a4640, 0, 3.78, 0);
  addMesh(g, G.box(1.4, 0.5, 0.02), 0x9a6a5a, -0.7, 3.3, 0.06);
  addMesh(g, G.box(2.4, 0.16, 0.02), 0x2a3a2a, 0.1, 2.7, 0.06);
  addMesh(g, G.box(1.8, 0.12, 0.02), 0x2a3a2a, -0.2, 2.45, 0.06);
  addMesh(g, G.box(0.7, 0.7, 0.02), 0xc8553d, 0.95, 3.3, 0.06).rotation.z = 0.785;
};
// (scrap_fence, the loose fence round a camp, is drawn with the town kit's walls: town-models.ts)
// cottages patched with salvaged sheet: a rusty panel or two over the old roof
{
  const base = BUILDERS.cottage;
  BUILDERS.cottage = g => {
    base(g);
    const a = CORRUGATED(1.2, 0.9, 0x8a5236); a.position.set(0.5, 2.18, 0.55); a.rotation.set(-0.6, 0, 0); g.add(a);
    const b = CORRUGATED(0.8, 0.7, 0x7a7a72); b.position.set(-0.6, 2.25, -0.55); b.rotation.set(0.6, 0.2, 0); g.add(b);
    addMesh(g, G.cyl(0.25, 0.25, 0.7, 10), 0x4a5a5a, 1.45, 0.35, -0.9); // a rain barrel
  };
}

/* ---------------- raider camps ---------------- */
BUILDERS.tent = g => {
  // a lean-to of tarp and poles, a bedroll under it
  const c = [0x5a5a3a, 0x6a4a3a, 0x4a5a5a][Math.floor(Math.random() * 3)];
  const a = addMesh(g, G.box(1.6, 0.05, 1.3), c, 0, 0.62, -0.25); a.rotation.x = 0.62;
  const b = addMesh(g, G.box(1.6, 0.05, 1.3), new THREE.Color(c).multiplyScalar(0.8).getHex(), 0, 0.62, 0.25); b.rotation.x = -0.62;
  for (const x of [-0.78, 0.78]) addMesh(g, G.box(0.06, 1.15, 0.06), 0x5a4430, x, 0.58, 0);
  addMesh(g, G.box(1.7, 0.05, 0.05), 0x5a4430, 0, 1.14, 0);
  addMesh(g, G.round(0.5, 0.12, 1.0, 0.05), 0x7a3a2a, 0.1, 0.06, 0.05);
};
BUILDERS.hull = g => {
  // an upturned boat hull the wreckers live under
  const hull = new THREE.Group(); g.add(hull); hull.rotation.z = Math.PI; hull.position.y = 1.1;
  addMesh(hull, G.round(3.4, 0.5, 1.6, 0.25), 0x4a3a2a, 0, 0, 0);
  for (let i = 0; i < 6; i++) addMesh(hull, G.box(0.08, 0.55, 1.62), 0x3a2a1e, -1.4 + i * 0.56, 0.02, 0);
  addMesh(hull, G.box(3.5, 0.1, 0.12), 0x2a2018, 0, -0.25, 0);
  for (const x of [-1.4, 1.4]) for (const z of [-0.7, 0.7]) addMesh(g, G.box(0.1, 0.9, 0.1), 0x5a4430, x, 0.45, z);
  addMesh(g, G.box(1.2, 0.04, 0.7), 0x6a6a5a, 0.6, 0.02, 1.0).rotation.y = 0.3; // an old sail as a mat
};
BUILDERS.cart_tipped = g => {
  // a cart on its side across the verge, one wheel in the air; crows on it
  const cart = new THREE.Group(); g.add(cart); cart.rotation.z = 1.35; cart.position.set(0, 0.55, 0);
  addMesh(cart, G.box(1.4, 0.1, 0.9), 0x7a5634, 0, 0, 0);
  for (const z of [-0.45, 0.45]) addMesh(cart, G.box(1.4, 0.35, 0.06), 0x6a4a2e, 0, 0.18, z);
  const w = addMesh(cart, G.cyl(0.34, 0.34, 0.08, 10), 0x4a3a2a, 0, -0.1, 0.55); w.rotation.x = Math.PI / 2;
  const w2 = addMesh(g, G.cyl(0.34, 0.34, 0.08, 10), 0x4a3a2a, 0.9, 0.04, -0.5); w2.rotation.z = Math.PI / 2; w2.rotation.y = 0.4;
  for (let i = 0; i < 3; i++) addMesh(g, G.round(0.32, 0.22, 0.28, 0.06), 0x9a7a52, -0.6 + i * 0.4, 0.11, 0.8 + (i % 2) * 0.2).rotation.y = i;
  for (const [x, y, z] of [[0.05, 1.3, 0.2], [-0.2, 1.28, -0.25]]) { addMesh(g, G.round(0.14, 0.12, 0.24, 0.05), 0x1a1a1e, x, y, z); addMesh(g, G.cone(0.03, 0.08, 4), 0x2a2a2a, x, y + 0.02, z + 0.15).rotation.x = Math.PI / 2; }
};
BUILDERS.stash = g => {
  // crates under a lashed tarp, a padlock that doesn't lock
  addMesh(g, G.round(0.9, 0.55, 0.7, 0.04), 0x7a5a36, 0, 0.28, 0);
  addMesh(g, G.round(0.6, 0.4, 0.5, 0.04), 0x8a6a3e, 0.15, 0.75, -0.05);
  const tarp = addMesh(g, G.box(1.1, 0.04, 0.95), 0x4a5a3a, -0.05, 0.98, 0); tarp.rotation.z = 0.12;
  addMesh(g, G.box(0.03, 0.9, 0.97), 0x2a2a2a, 0.35, 0.5, 0);
  addMesh(g, G.round(0.12, 0.14, 0.05, 0.02), 0xc8a040, 0, 0.32, 0.37);
};

/* ---------------- salvage you mine (see SALVAGE_MODEL in world-view) ---------------- */
BUILDERS.rails = g => {
  // a stretch of old track: two rusted rails on rotten sleepers, one bent up out of the ground
  for (let i = 0; i < 4; i++) addMesh(g, G.box(0.18, 0.06, 1.1), 0x4a3a2a, -0.6 + i * 0.4, 0.03, 0).rotation.y = (i % 2) * 0.08;
  for (const z of [-0.36, 0.36]) addMesh(g, G.box(1.7, 0.08, 0.07), 0x6a4a36, 0, 0.1, z);
  const bent = addMesh(g, G.box(0.9, 0.08, 0.07), 0x7a5236, 0.95, 0.32, 0.36); bent.rotation.z = 0.5;
  addMesh(g, G.dod(0.12), 0x6a6460, -0.3, 0.06, 0.6);
};
BUILDERS.aircraft = g => {
  // a broken piece of an aircraft: a fuselage section, a wing stub, a fin, half buried
  const body = addMesh(g, G.cyl(0.55, 0.6, 2.2, 10), 0xc8ccd2, 0, 0.42, 0); body.rotation.z = Math.PI / 2; body.rotation.y = 0.2;
  addMesh(g, G.box(0.06, 0.5, 0.5), 0x1e1e20, 1.1, 0.42, 0.2); // the torn end
  for (let i = 0; i < 3; i++) addMesh(g, G.box(0.2, 0.12, 0.02), 0x2a3a4a, -0.6 + i * 0.4, 0.6, 0.55); // windows
  const wing = addMesh(g, G.box(1.4, 0.06, 0.5), 0xb8bcc2, -0.2, 0.18, -0.9); wing.rotation.y = 0.3; wing.rotation.z = -0.12;
  const fin = addMesh(g, G.box(0.5, 0.7, 0.05), 0xb0b4ba, -1.0, 0.85, 0); fin.rotation.z = 0.3;
  addMesh(g, G.box(0.3, 0.08, 0.2), 0xb84a3a, -1.0, 1.05, 0.03); // a stripe of old paint
  addMesh(g, G.ico(0.25), 0x4a6a32, 0.6, 0.12, 0.7).scale.set(1.4, 0.4, 1);
};
BUILDERS.plating = g => {
  // a fallen wall panel from the Facility: ribbed grey plate, bolts, a faded stencil
  const p = addMesh(g, G.box(1.4, 0.12, 1.0), 0x8a9a96, 0, 0.2, 0); p.rotation.z = 0.2;
  for (let i = 0; i < 4; i++) addMesh(g, G.box(0.06, 0.06, 0.96), 0x6a7a76, -0.5 + i * 0.33, 0.32 + i * 0.06, 0).rotation.z = 0.2;
  addMesh(g, G.box(0.4, 0.02, 0.24), 0xd8b030, 0.2, 0.36, 0.2).rotation.z = 0.2;
  addMesh(g, G.box(0.9, 0.8, 0.12), 0x7a8a86, -0.2, 0.4, -0.55).rotation.x = -0.15;
};
BUILDERS.core = g => {
  // the Facility's core: a squat armoured cylinder, cracked open, glowing a little inside
  addMesh(g, G.cyl(0.6, 0.7, 1.1, 12), 0x5a6a68, 0, 0.55, 0);
  addMesh(g, G.cyl(0.66, 0.66, 0.1, 12), 0x3a4a48, 0, 0.6, 0);
  addMesh(g, G.cyl(0.66, 0.66, 0.1, 12), 0x3a4a48, 0, 0.95, 0);
  const glow = addMesh(g, G.box(0.08, 0.7, 0.3), 0x8affe0, 0.62, 0.55, 0, { emissive: 0x4ae8c0 }); glow.castShadow = false;
  addMesh(g, G.box(0.5, 0.06, 0.5), 0x2a3a38, 0, 1.13, 0);
  for (let i = 0; i < 3; i++) addMesh(g, G.dod(0.14), 0x4a5a58, Math.cos(i * 2) * 0.9, 0.08, Math.sin(i * 2) * 0.9);
};
